import axios from "axios";

const API_BASE_URL = import.meta.env.VITE_API_URL;

const api = axios.create({
	baseURL: API_BASE_URL,
	withCredentials: true,
	timeout: 30000,
	headers: {
		"Content-Type": "application/json",
	},
});

// Handle 401 responses globally (expired/invalid cookie)
api.interceptors.response.use(
	(response) => response,
	(error) => {
		if (error.response?.status === 401) {
			localStorage.removeItem("gms_user");
			if (window.location.pathname !== "/login") {
				window.location.href = "/login";
			}
		}
		return Promise.reject(error);
	},
);

/**
 * Normalize any API error into a consistent shape for Redux thunks.
 * Returns { message, code, details? } — always serializable.
 */
export function normalizeError(error) {
	// Network error — server unreachable / no internet
	if (!error.response) {
		if (error.code === "ECONNABORTED" || error.message?.includes("timeout")) {
			return {
				message: "Request timed out. Please try again.",
				code: "TIMEOUT",
			};
		}
		return {
			message:
				"Unable to reach the server. Please check your connection and try again.",
			code: "NETWORK_ERROR",
		};
	}

	const { status, data } = error.response;

	// Validation error
	if (status === 400) {
		return {
			message: data?.error || "Invalid request. Please check your input.",
			code: "VALIDATION_ERROR",
			details: data?.details || null,
		};
	}

	// Auth errors (handled by interceptor redirect, but just in case)
	if (status === 401) {
		return {
			message: "Your session has expired. Please log in again.",
			code: "UNAUTHORIZED",
		};
	}

	// Forbidden
	if (status === 403) {
		return {
			message:
				data?.error || "You don't have permission to perform this action.",
			code: "FORBIDDEN",
		};
	}

	// Not found
	if (status === 404) {
		return {
			message: data?.error || "The requested resource was not found.",
			code: "NOT_FOUND",
		};
	}

	// Server error
	if (status >= 500) {
		return {
			message: "Something went wrong on our end. Please try again later.",
			code: "SERVER_ERROR",
		};
	}

	// Fallback
	return {
		message: data?.error || "An unexpected error occurred.",
		code: "UNKNOWN",
	};
}

export default api;
